import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { resolveMedia } from '../../context/SettingsContext';

const STATUSES = ['placed', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled', 'returned'];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');
  const [tracking, setTracking] = useState('');
  const [saving, setSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [address, setAddress] = useState({
    fullName: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '', country: 'India',
  });

  const fetchOrder = () => {
    api.get(`/orders/${id}`).then((res) => {
      setOrder(res.data);
      setStatus(res.data.orderStatus);
      setTracking(res.data.trackingNumber || '');
      setAddress({
        fullName: res.data.shippingAddress?.fullName || res.data.user?.name || '',
        phone: res.data.shippingAddress?.phone || res.data.user?.phone || '',
        line1: res.data.shippingAddress?.line1 || '',
        line2: res.data.shippingAddress?.line2 || '',
        city: res.data.shippingAddress?.city || '',
        state: res.data.shippingAddress?.state || '',
        pincode: res.data.shippingAddress?.pincode || '',
        country: res.data.shippingAddress?.country || 'India',
      });
    });
  };

  useEffect(fetchOrder, [id]);

  const updateStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/orders/${id}/status`, { status, note, trackingNumber: tracking });
      toast.success('Order status updated');
      setNote('');
      fetchOrder();
    } catch (e) {
      toast.error('Could not update status');
    } finally {
      setSaving(false);
    }
  };

  const updatePaymentStatus = async (paymentStatus) => {
    setPaymentSaving(true);
    try {
      await api.patch(`/orders/${id}/payment-status`, { paymentStatus });
      toast.success(paymentStatus === 'paid' ? 'Payment recorded and invoice generated' : 'Payment status updated');
      fetchOrder();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update payment status');
    } finally {
      setPaymentSaving(false);
    }
  };

  const updateAddress = (field, value) => {
    setAddress((current) => ({ ...current, [field]: value }));
  };

  const saveShippingAddress = async (event) => {
    event.preventDefault();
    setAddressSaving(true);
    try {
      await api.patch(`/orders/${id}/shipping-address`, address);
      setEditingAddress(false);
      toast.success('Shipping address updated');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not update shipping address');
    } finally {
      setAddressSaving(false);
    }
  };

  if (!order) return <div className="p-8 text-primaryDark/50">Loading...</div>;

  return (
    <div className="p-3 sm:p-8 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl text-primaryDark">Order {order.orderNumber}</h1>
        {order.paymentStatus === 'paid' && <Link to={`/orders/${order._id}/invoice`} target="_blank" className="btn-outline text-center">View Invoice</Link>}
      </div>

      <div className="card p-6 mb-6">
        <h3 className="font-semibold mb-3">Items</h3>
        {order.items.map((item, i) => (
          <div key={i} className="flex gap-4 py-3 border-b border-primaryBrown/10 last:border-0 text-sm">
            <div className="w-14 h-14 bg-bgCream rounded overflow-hidden shrink-0">
              {item.image && <img src={resolveMedia(item.image)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1">
              <p className="font-medium">{item.name}</p>
              {item.variantLabel && <p className="text-xs text-primaryDark/50">{item.variantLabel}</p>}
              <p className="text-primaryDark/60">Qty: {item.qty} × ₹{item.price}</p>
            </div>
            <div className="font-semibold">₹{item.price * item.qty}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="card p-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-semibold">Customer & Delivery</h3>
            {!editingAddress && (
              <button type="button" onClick={() => setEditingAddress(true)} className="text-xs font-semibold text-primaryBrown hover:text-primaryDark">
                Edit address
              </button>
            )}
          </div>
          {!editingAddress ? (
            <div>
              <p className="text-sm font-medium mb-1">{order.shippingAddress?.fullName || order.user?.name}</p>
              <p className="text-xs text-primaryDark/60 mb-3">{order.shippingAddress?.phone} • {order.user?.email}</p>
              <p className="text-sm text-primaryDark/70 leading-6">
                {order.shippingAddress?.line1}
                {order.shippingAddress?.line2 && <>, {order.shippingAddress.line2}</>}<br />
                {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.pincode}<br />
                {order.shippingAddress?.country || 'India'}
              </p>
              {order.shippingAddress?.lat != null && order.shippingAddress?.lng != null ? (
                <div className="mt-3 text-xs text-primaryDark/50">
                  <p>Live location: {order.shippingAddress.lat.toFixed(4)}, {order.shippingAddress.lng.toFixed(4)}</p>
                  {order.distanceKm != null && <p>Distance from warehouse: {order.distanceKm} km</p>}
                </div>
              ) : (
                <p className="mt-3 text-xs text-primaryDark/50">Address entered manually</p>
              )}
            </div>
          ) : (
            <form onSubmit={saveShippingAddress} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs font-medium sm:col-span-2">Full name
                  <input required className="input-field mt-1" value={address.fullName} onChange={(e) => updateAddress('fullName', e.target.value)} />
                </label>
                <label className="text-xs font-medium sm:col-span-2">Phone number
                  <input required inputMode="tel" className="input-field mt-1" value={address.phone} onChange={(e) => updateAddress('phone', e.target.value)} />
                </label>
                <label className="text-xs font-medium sm:col-span-2">Address line 1
                  <input required className="input-field mt-1" value={address.line1} onChange={(e) => updateAddress('line1', e.target.value)} />
                </label>
                <label className="text-xs font-medium sm:col-span-2">Address line 2
                  <input className="input-field mt-1" value={address.line2} onChange={(e) => updateAddress('line2', e.target.value)} />
                </label>
                <label className="text-xs font-medium">City
                  <input required className="input-field mt-1" value={address.city} onChange={(e) => updateAddress('city', e.target.value)} />
                </label>
                <label className="text-xs font-medium">State
                  <input required className="input-field mt-1" value={address.state} onChange={(e) => updateAddress('state', e.target.value)} />
                </label>
                <label className="text-xs font-medium">Pincode
                  <input required inputMode="numeric" maxLength="6" className="input-field mt-1" value={address.pincode} onChange={(e) => updateAddress('pincode', e.target.value.replace(/\D/g, ''))} />
                </label>
                <label className="text-xs font-medium">Country
                  <input required className="input-field mt-1" value={address.country} onChange={(e) => updateAddress('country', e.target.value)} />
                </label>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={addressSaving} className="btn-primary px-4 py-2">{addressSaving ? 'Saving...' : 'Save address'}</button>
                <button type="button" disabled={addressSaving} onClick={() => { setEditingAddress(false); fetchOrder(); }} className="btn-outline px-4 py-2">Cancel</button>
              </div>
            </form>
          )}
        </div>

        <div className="card p-6">
          <h3 className="font-semibold mb-3">Payment</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span>Items Total</span><span>₹{order.itemsTotal}</span></div>
            {order.discountTotal > 0 && <div className="flex justify-between text-emerald-700"><span>Promo discount{order.promoCode ? ` (${order.promoCode})` : ''}</span><span>-₹{order.discountTotal}</span></div>}
            <div className="flex justify-between"><span>Shipping</span><span>₹{order.shippingCost}</span></div>
            <div className="flex justify-between font-semibold border-t border-primaryBrown/10 pt-2 mt-2"><span>Total</span><span>₹{order.totalAmount}</span></div>
          </div>
          <p className="text-xs mt-3 capitalize">{order.paymentMethod} — {order.paymentStatus}</p>
          <div className="flex gap-2 mt-4">
            {order.paymentStatus !== 'paid' && (
              <button type="button" disabled={paymentSaving} onClick={() => updatePaymentStatus('paid')} className="btn-primary px-4 py-2">
                {paymentSaving ? 'Saving...' : 'Mark Paid'}
              </button>
            )}
            {order.paymentStatus === 'paid' && (
              <button type="button" disabled={paymentSaving} onClick={() => updatePaymentStatus('refunded')} className="btn-outline px-4 py-2">
                Mark Refunded
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card p-6">
        <h3 className="font-semibold mb-3">Update Order Status</h3>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <select className="input-field" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input placeholder="Tracking number (optional)" className="input-field" value={tracking} onChange={(e) => setTracking(e.target.value)} />
          <input placeholder="Note (optional)" className="input-field" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <button onClick={updateStatus} disabled={saving} className="btn-primary">{saving ? 'Updating...' : 'Update Status'}</button>
      </div>
    </div>
  );
}
